import { Header } from './MichelaLanding/Header'
import { Footer } from './MichelaLanding/Footer'

export function PrivacyPolicy() {
    const lastUpdated = 'December 18, 2024'

    return (
        <div className="min-h-screen bg-paper text-ink">
            <Header />
            <main className="max-w-4xl mx-auto px-6 sm:px-8 py-16 sm:py-24">
                <article className="prose prose-lg max-w-none">
                    <h1 className="font-display text-4xl sm:text-5xl font-semibold text-ink mb-4">
                        Privacy Policy
                    </h1>
                    <p className="text-ink-tertiary text-lg mb-12">
                        Last updated: {lastUpdated}
                    </p>

                    <section className="mb-12">
                        <h2 className="font-display text-2xl font-semibold text-ink mb-4">
                            Introduction
                        </h2>
                        <p className="text-ink-secondary mb-4 leading-relaxed">
                            Michela ("we," "our," or "us") is committed to protecting your privacy. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use our mobile application and website (collectively, the "Service").
                        </p>
                        <p className="text-ink-secondary mb-4 leading-relaxed">
                            Please read this Privacy Policy carefully. By using the Service, you agree to the collection and use of information in accordance with this policy. If you do not agree with the terms of this Privacy Policy, please do not access the Service.
                        </p>
                    </section>

                    <section className="mb-12">
                        <h2 className="font-display text-2xl font-semibold text-ink mb-4">
                            Information We Collect
                        </h2>

                        <h3 className="font-display text-xl font-medium text-ink mb-3 mt-6">
                            Personal Information You Provide
                        </h3>
                        <p className="text-ink-secondary mb-4 leading-relaxed">
                            We collect information you provide directly to us, including:
                        </p>
                        <ul className="list-disc pl-6 text-ink-secondary mb-4 space-y-2">
                            <li><strong>Account Information:</strong> Email address, password, and profile details when you create an account</li>
                            <li><strong>Wardrobe Photos:</strong> Images of your clothing items that you upload to build your digital wardrobe</li>
                            <li><strong>Mirror/Selfie Photos:</strong> Photos you provide for virtual try-on features (Pro feature)</li>
                            <li><strong>Style Preferences:</strong> Your style goals, color preferences, budget ranges, and preferred stores</li>
                            <li><strong>Waitlist Information:</strong> Email address and styling interests when you join our waitlist</li>
                            <li><strong>Feedback and Ratings:</strong> Your outfit ratings and feedback to improve recommendations</li>
                        </ul>

                        <h3 className="font-display text-xl font-medium text-ink mb-3 mt-6">
                            Information Collected Automatically
                        </h3>
                        <p className="text-ink-secondary mb-4 leading-relaxed">
                            When you use our Service, we automatically collect certain information:
                        </p>
                        <ul className="list-disc pl-6 text-ink-secondary mb-4 space-y-2">
                            <li><strong>Device Information:</strong> Device type, operating system, unique device identifiers</li>
                            <li><strong>Usage Data:</strong> Features used, actions taken, time spent, and interaction patterns</li>
                            <li><strong>Location Data:</strong> Approximate location (with your permission) to provide weather-appropriate outfit suggestions</li>
                            <li><strong>Log Data:</strong> IP address, browser type, access times, and error logs for troubleshooting</li>
                        </ul>

                        <h3 className="font-display text-xl font-medium text-ink mb-3 mt-6">
                            Information from Third Parties
                        </h3>
                        <p className="text-ink-secondary mb-4 leading-relaxed">
                            If you sign in using Google or Apple, we receive basic profile information (name and email) as permitted by your account settings.
                        </p>
                    </section>

                    <section className="mb-12">
                        <h2 className="font-display text-2xl font-semibold text-ink mb-4">
                            How We Use Your Information
                        </h2>
                        <p className="text-ink-secondary mb-4 leading-relaxed">
                            We use the information we collect to:
                        </p>
                        <ul className="list-disc pl-6 text-ink-secondary mb-4 space-y-2">
                            <li>Provide, maintain, and improve our Service</li>
                            <li>Generate personalized outfit recommendations using AI</li>
                            <li>Create virtual try-on images (Pro feature)</li>
                            <li>Analyze your color profile for better style suggestions</li>
                            <li>Process transactions and manage subscriptions</li>
                            <li>Send you updates, security alerts, and support messages</li>
                            <li>Understand how you use the Service to improve user experience</li>
                            <li>Detect, investigate, and prevent fraudulent or unauthorized activity</li>
                            <li>Comply with legal obligations</li>
                        </ul>
                    </section>

                    <section className="mb-12">
                        <h2 className="font-display text-2xl font-semibold text-ink mb-4">
                            AI Processing
                        </h2>
                        <p className="text-ink-secondary mb-4 leading-relaxed">
                            Michela uses artificial intelligence to power outfit recommendations, virtual try-ons, and color analysis. Here's how we handle AI processing:
                        </p>
                        <ul className="list-disc pl-6 text-ink-secondary mb-4 space-y-2">
                            <li><strong>Wardrobe Analysis:</strong> Your clothing photos are analyzed by AI to identify colors, styles, and categories</li>
                            <li><strong>Outfit Generation:</strong> AI generates outfit combinations based on your wardrobe, preferences, and context (weather, occasion)</li>
                            <li><strong>Virtual Try-On:</strong> Your mirror photos and wardrobe items may be processed by AI image models to create visualizations</li>
                            <li><strong>Color Analysis:</strong> Your selfie may be analyzed to determine your optimal color palette</li>
                        </ul>
                        <p className="text-ink-secondary mb-4 leading-relaxed">
                            AI processing is performed by third-party providers (including OpenRouter and OpenAI). These providers process your data according to their privacy policies and our data processing agreements. Your photos are not used to train AI models without your explicit consent.
                        </p>
                    </section>

                    <section className="mb-12">
                        <h2 className="font-display text-2xl font-semibold text-ink mb-4">
                            Data Storage and Security
                        </h2>
                        <p className="text-ink-secondary mb-4 leading-relaxed">
                            We implement appropriate technical and organizational security measures to protect your personal information:
                        </p>
                        <ul className="list-disc pl-6 text-ink-secondary mb-4 space-y-2">
                            <li>Your data is stored on secure servers provided by Supabase</li>
                            <li>All data transmission is encrypted using TLS/SSL</li>
                            <li>Photos are stored in secure cloud storage with access controls</li>
                            <li>We regularly review and update our security practices</li>
                        </ul>
                        <p className="text-ink-secondary mb-4 leading-relaxed">
                            While we strive to protect your personal information, no method of transmission over the Internet or electronic storage is 100% secure. We cannot guarantee absolute security.
                        </p>
                    </section>

                    <section className="mb-12">
                        <h2 className="font-display text-2xl font-semibold text-ink mb-4">
                            Third-Party Services
                        </h2>
                        <p className="text-ink-secondary mb-4 leading-relaxed">
                            We use the following third-party services to operate our Service:
                        </p>
                        <ul className="list-disc pl-6 text-ink-secondary mb-4 space-y-2">
                            <li><strong>Supabase:</strong> Authentication, database, and file storage</li>
                            <li><strong>RevenueCat:</strong> Subscription and payment processing</li>
                            <li><strong>PostHog:</strong> Product analytics and feature flags</li>
                            <li><strong>Sentry:</strong> Error tracking and performance monitoring</li>
                            <li><strong>OpenRouter/OpenAI:</strong> AI-powered features</li>
                            <li><strong>Cloudflare:</strong> Security and bot protection</li>
                            <li><strong>Apple/Google:</strong> App distribution and in-app purchases</li>
                        </ul>
                        <p className="text-ink-secondary mb-4 leading-relaxed">
                            Each of these services has its own privacy policy governing the use of your information. We encourage you to review their policies.
                        </p>
                    </section>

                    <section className="mb-12">
                        <h2 className="font-display text-2xl font-semibold text-ink mb-4">
                            Data Retention
                        </h2>
                        <p className="text-ink-secondary mb-4 leading-relaxed">
                            We retain your personal information for as long as your account is active or as needed to provide you with our Service. We may also retain certain information as required by law or for legitimate business purposes.
                        </p>
                        <p className="text-ink-secondary mb-4 leading-relaxed">
                            When you delete your account, we delete or anonymize your personal data, including:
                        </p>
                        <ul className="list-disc pl-6 text-ink-secondary mb-4 space-y-2">
                            <li>All wardrobe photos and mirror photos</li>
                            <li>Profile information and preferences</li>
                            <li>Generated outfits and try-on images</li>
                            <li>Analytics data associated with your account</li>
                            <li>Subscription information from payment providers</li>
                        </ul>
                    </section>

                    <section className="mb-12">
                        <h2 className="font-display text-2xl font-semibold text-ink mb-4">
                            Your Rights and Choices
                        </h2>
                        <p className="text-ink-secondary mb-4 leading-relaxed">
                            Depending on your location, you may have certain rights regarding your personal information:
                        </p>
                        <ul className="list-disc pl-6 text-ink-secondary mb-4 space-y-2">
                            <li><strong>Access:</strong> Request access to the personal information we hold about you</li>
                            <li><strong>Correction:</strong> Request correction of inaccurate or incomplete information</li>
                            <li><strong>Deletion:</strong> Request deletion of your personal information</li>
                            <li><strong>Data Portability:</strong> Request a copy of your data in a portable format</li>
                            <li><strong>Opt-out:</strong> Opt out of certain data collection (e.g., analytics, location)</li>
                            <li><strong>Withdraw Consent:</strong> Withdraw consent where processing is based on consent</li>
                        </ul>
                        <p className="text-ink-secondary mb-4 leading-relaxed">
                            To exercise these rights, you can use the account settings in our app or contact us at{' '}
                            <a href="mailto:privacy@michela.app" className="text-ink underline hover:text-ink-secondary transition-colors">
                                privacy@michela.app
                            </a>.
                        </p>
                    </section>

                    <section className="mb-12">
                        <h2 className="font-display text-2xl font-semibold text-ink mb-4">
                            Children's Privacy
                        </h2>
                        <p className="text-ink-secondary mb-4 leading-relaxed">
                            Our Service is not intended for children under 13 years of age. We do not knowingly collect personal information from children under 13. If we learn we have collected personal information from a child under 13, we will delete that information promptly.
                        </p>
                    </section>

                    <section className="mb-12">
                        <h2 className="font-display text-2xl font-semibold text-ink mb-4">
                            International Data Transfers
                        </h2>
                        <p className="text-ink-secondary mb-4 leading-relaxed">
                            Your information may be transferred to and processed in countries other than your country of residence. These countries may have data protection laws that are different from your country. We take steps to ensure that your information receives adequate protection in accordance with this Privacy Policy.
                        </p>
                    </section>

                    <section className="mb-12">
                        <h2 className="font-display text-2xl font-semibold text-ink mb-4">
                            California Privacy Rights
                        </h2>
                        <p className="text-ink-secondary mb-4 leading-relaxed">
                            If you are a California resident, you have additional rights under the California Consumer Privacy Act (CCPA):
                        </p>
                        <ul className="list-disc pl-6 text-ink-secondary mb-4 space-y-2">
                            <li>Right to know what personal information we collect, use, and share</li>
                            <li>Right to delete your personal information</li>
                            <li>Right to opt-out of the sale of personal information (we do not sell your personal information)</li>
                            <li>Right to non-discrimination for exercising your privacy rights</li>
                        </ul>
                    </section>

                    <section className="mb-12">
                        <h2 className="font-display text-2xl font-semibold text-ink mb-4">
                            European Privacy Rights (GDPR)
                        </h2>
                        <p className="text-ink-secondary mb-4 leading-relaxed">
                            If you are in the European Economic Area (EEA), you have rights under the General Data Protection Regulation (GDPR). Our legal bases for processing your information include:
                        </p>
                        <ul className="list-disc pl-6 text-ink-secondary mb-4 space-y-2">
                            <li><strong>Contract:</strong> Processing necessary for the performance of our contract with you</li>
                            <li><strong>Legitimate Interests:</strong> Processing for our legitimate business interests</li>
                            <li><strong>Consent:</strong> Processing based on your consent (which you may withdraw)</li>
                            <li><strong>Legal Obligation:</strong> Processing necessary to comply with legal requirements</li>
                        </ul>
                    </section>

                    <section className="mb-12">
                        <h2 className="font-display text-2xl font-semibold text-ink mb-4">
                            Changes to This Privacy Policy
                        </h2>
                        <p className="text-ink-secondary mb-4 leading-relaxed">
                            We may update this Privacy Policy from time to time. We will notify you of any changes by posting the new Privacy Policy on this page and updating the "Last updated" date. We encourage you to review this Privacy Policy periodically.
                        </p>
                    </section>

                    <section className="mb-12">
                        <h2 className="font-display text-2xl font-semibold text-ink mb-4">
                            Contact Us
                        </h2>
                        <p className="text-ink-secondary mb-4 leading-relaxed">
                            If you have any questions about this Privacy Policy or our privacy practices, please contact us:
                        </p>
                        <ul className="list-none text-ink-secondary mb-4 space-y-2">
                            <li>
                                <strong>Email:</strong>{' '}
                                <a href="mailto:privacy@michela.app" className="text-ink underline hover:text-ink-secondary transition-colors">
                                    privacy@michela.app
                                </a>
                            </li>
                            <li>
                                <strong>General inquiries:</strong>{' '}
                                <a href="mailto:hello@michela.app" className="text-ink underline hover:text-ink-secondary transition-colors">
                                    hello@michela.app
                                </a>
                            </li>
                        </ul>
                    </section>
                </article>
            </main>
            <Footer />
        </div>
    )
}
